import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  FileDownload,
  ContentCopy,
  WhatsApp,
  Phone,
  PersonOff,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  RestartAlt,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import {
  headingBlue,
  maroon,
  mediumGrey,
  lightGrey,
  pureWhite,
  green,
  orange,
  amber,
  Donegreen,
  lightRed,
  lightBlue,
  skyBlue,
  pink,
} from '../../../assets/colors';
import { buttonStyle, button2Style } from '../../../utils/commonCSS';
import CustomTextField from '../../CustomTextField';
import CustomDropDown from '../../CustomDropDown';
import { tableOffsets } from '../../../utils/constants';

import logoBase64 from '../../../assets/images/WORK_logo.png';
import compLogoBase64 from '../../../assets/images/compassionMonth.png';

import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../CustomComponents/CustomComponentsSlice';
import Loader from '../../Loader';
import HttpService from '../../../backend/requestApis';
import {
  fetchPollVotingStats,
  fetchPollStateVotingStats,
  fetchPollDistrictVotingStats,
  fetchPollVotingMembers,
  fetchStatesForHead,
  fetchDistrictsForHead,
} from '../../../backend/backendApis';

// --- CONSISTENT REPO STYLES ---
const outerBoxStyle = {
  width: '100%',
  marginTop: '0.2rem',
};

const paperCardStyle = {
  border: '1px solid #D9D9D9',
  padding: '0.3rem 0.2rem',
  boxShadow: '-0.05rem 0.05rem 0.1rem 0.1rem #D9D9D9',
  backgroundColor: pureWhite,
};

const topBarBoxStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  border: '1px solid #D9D9D9',
  backgroundColor: pureWhite,
  padding: '0.2rem 0.6rem',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '-0.05rem 0.05rem 0.1rem 0.1rem #D9D9D9',
};

const headerCellStyle = {
  padding: '0.2rem 0.4rem',
  fontSize: '0.82rem',
  fontWeight: '600',
  textAlign: 'center',
  backgroundColor: lightGrey,
  color: headingBlue,
  borderBottom: '1px solid #D9D9D9',
};

const scrollbarStyle = {
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
};

const formatCategoryDisplay = (cat) => {
  if (!cat) return '';
  const lower = String(cat).toLowerCase().trim();
  if (lower === 'committed') return 'Committed WORKers';
  if (lower === 'uncommitted' || lower === 'noncommitted') return 'Supported WORKer';
  if (lower === 'all' || lower === 'all workers') return 'All WORKers';
  return cat;
};

const PollStats = ({
  pollId,
  pollDetails,
  programName,
  status,
  WORKerCategory,
  createdDate,
  expiryDate,
  description,
  totalEligible,
  totalVoted,
  totalNonVoted,
  onBack,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const routeParams = useParams();
  const location = useLocation();
  const qrRef = useRef(null);

  const id = pollId || pollDetails?._id || routeParams?.id;
  const logo = pollDetails?.logo || routeParams?.logo || 'WORK';

  // --- API STATE FOR POLL VOTING STATISTICS ---
  const [votingStats, setVotingStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // --- API STATE FOR STATE-WISE VOTING STATISTICS ---
  const [stateVotingStats, setStateVotingStats] = useState(null);
  const [isStateStatsLoading, setIsStateStatsLoading] = useState(false);

  // --- API STATE FOR DISTRICT-WISE VOTING STATISTICS ---
  const [districtVotingStats, setDistrictVotingStats] = useState(null);
  const [isDistrictStatsLoading, setIsDistrictStatsLoading] = useState(false);

  // --- API STATE FOR POLL VOTING MEMBERS TABLE ---
  const [membersTableData, setMembersTableData] = useState(null);
  const [isMembersLoading, setIsMembersLoading] = useState(false);

  // Integration Agent API Handler: Fetch Poll Voting Statistics (Pie Chart & Overall Breakdown)
  const fetchVotingStatistics = async (
    pollId,
    stateFilter = 'All States',
    districtFilter = 'All Districts'
  ) => {
    if (!pollId) return;
    setIsLoading(true);
    const token = localStorage.getItem('token');
    const params = {};
    if (stateFilter && stateFilter !== 'All States') {
      params.state = stateFilter;
    }
    if (districtFilter && districtFilter !== 'All Districts') {
      params.district = districtFilter;
    }

    const options = {
      url: fetchPollVotingStats + pollId,
      method: 'GET',
      params,
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    };

    try {
      const response = await HttpService(options);
      if (response?.data?.data) {
        setVotingStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching poll voting statistics:', err);
      const status = err?.response?.status || err?.status;
      if (status === 409) {
        setIsLoading(false);
        navigate('/Login');
        return;
      } else if (status === 401 || status === 403) {
        localStorage.clear();
        navigate('/Login');
        return;
      } else {
        const errorMsg =
          err?.response?.data?.description ||
          err?.response?.data?.message ||
          'Failed to fetch poll voting statistics.';
        dispatch(showSnackBar({ message: errorMsg, severity: 'error' }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Integration Agent API Handler: Fetch State-Wise Poll Voting Statistics
  const fetchStateVotingStatistics = async (pollId, pageNum = 1, pageSize = 20, stateFilter = 'All States') => {
    if (!pollId) return;
    setIsStateStatsLoading(true);
    const token = localStorage.getItem('token');
    const params = {
      page: pageNum,
      limit: pageSize,
    };
    if (stateFilter && stateFilter !== 'All States') {
      params.state = stateFilter;
    }
    const options = {
      url: fetchPollStateVotingStats + pollId,
      method: 'GET',
      params,
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    };

    try {
      const response = await HttpService(options);
      if (response?.data?.data) {
        setStateVotingStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching state-wise poll voting statistics:', err);
      const status = err?.response?.status || err?.status;
      if (status === 409) {
        setIsStateStatsLoading(false);
        navigate('/Login');
        return;
      } else if (status === 401 || status === 403) {
        localStorage.clear();
        navigate('/Login');
        return;
      } else {
        const errorMsg =
          err?.response?.data?.description ||
          err?.response?.data?.message ||
          'Failed to fetch state-wise poll voting statistics.';
        dispatch(showSnackBar({ message: errorMsg, severity: 'error' }));
      }
    } finally {
      setIsStateStatsLoading(false);
    }
  };

  // Integration Agent API Handler: Fetch District-Wise Poll Voting Statistics
  const fetchDistrictVotingStatistics = async (
    pollId,
    pageNum = 1,
    pageSize = 15,
    stateFilter = 'All States',
    districtFilter = 'All Districts'
  ) => {
    if (!pollId) return;
    setIsDistrictStatsLoading(true);
    const token = localStorage.getItem('token');
    const params = {
      page: pageNum,
      limit: pageSize,
    };
    if (stateFilter && stateFilter !== 'All States') {
      params.state = stateFilter;
    }
    if (districtFilter && districtFilter !== 'All Districts') {
      params.district = districtFilter;
    }

    const options = {
      url: fetchPollDistrictVotingStats + pollId,
      method: 'GET',
      params,
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    };

    try {
      const response = await HttpService(options);
      if (response?.data?.data) {
        setDistrictVotingStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching district-wise poll voting statistics:', err);
      const status = err?.response?.status || err?.status;
      if (status === 409) {
        setIsDistrictStatsLoading(false);
        navigate('/Login');
        return;
      } else if (status === 401) {
        localStorage.clear();
        navigate('/Login');
        return;
      } else if (status === 403) {
        const description = err?.response?.data?.description || err?.response?.data?.message || '';
        if (/District Heads are not authorized/i.test(description)) {
          dispatch(showSnackBar({ message: description, severity: 'warning' }));
        } else {
          localStorage.clear();
          navigate('/Login');
        }
        return;
      } else {
        const errorMsg =
          err?.response?.data?.description ||
          err?.response?.data?.message ||
          'Failed to fetch district-wise poll voting statistics.';
        dispatch(showSnackBar({ message: errorMsg, severity: 'error' }));
      }
    } finally {
      setIsDistrictStatsLoading(false);
    }
  };

  // Integration Agent API Handler: Fetch Poll Voting Members Table
  const fetchPollVotingMembersData = async (
    pollId,
    pageNum = 1,
    limitSize = 15,
    statusType = 'notVoted',
    stateFilter = 'All States',
    districtFilter = 'All Districts',
    search = ''
  ) => {
    if (!pollId) return;
    setIsMembersLoading(true);
    const token = localStorage.getItem('token');
    const params = {
      page: pageNum,
      limit: limitSize,
      status: statusType === 'nonVoted' ? 'notVoted' : statusType,
    };
    if (stateFilter && stateFilter !== 'All States') {
      params.state = stateFilter;
    }
    if (districtFilter && districtFilter !== 'All Districts') {
      params.district = districtFilter;
    }
    if (search && search.trim() !== '') {
      params.searchText = search.trim();
    }

    const options = {
      url: fetchPollVotingMembers + pollId,
      method: 'GET',
      params,
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    };

    try {
      const response = await HttpService(options);
      if (response?.data?.data) {
        setMembersTableData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching poll voting members table:', err);
      const status = err?.response?.status || err?.status;
      if (status === 409) {
        setIsMembersLoading(false);
        navigate('/Login');
        return;
      } else if (status === 401 || status === 403) {
        localStorage.clear();
        navigate('/Login');
        return;
      } else {
        const errorMsg =
          err?.response?.data?.description ||
          err?.response?.data?.message ||
          'Failed to fetch poll voting members.';
        dispatch(showSnackBar({ message: errorMsg, severity: 'error' }));
      }
    } finally {
      setIsMembersLoading(false);
    }
  };

  // Content for top detail bar: derived from API votingStats, props, router state
  const routerState = location?.state?.pollDetails || location?.state || {};
  const activePollDetails = {
    programName:
      votingStats?.programName ||
      programName ||
      pollDetails?.programName ||
      routerState?.programName ||
      'NDM Poll',
    status:
      status ||
      pollDetails?.status ||
      routerState?.status ||
      (pollDetails?.isActive ? 'Active' : 'Active'),
    WORKerCategory:
      votingStats?.WORKerCategory ||
      WORKerCategory ||
      pollDetails?.WORKerCategory ||
      routerState?.WORKerCategory ||
      '',
    createdDate:
      createdDate ||
      pollDetails?.createdDate ||
      (pollDetails?.programDate ? pollDetails.programDate.split('T')[0] : '') ||
      (pollDetails?.createdAt ? pollDetails.createdAt.split('T')[0] : '') ||
      routerState?.createdDate ||
      (routerState?.programDate ? routerState.programDate.split('T')[0] : '') ||
      '',
    expiryDate:
      expiryDate ||
      pollDetails?.expiryDate ||
      routerState?.expiryDate ||
      '',
    description:
      description ||
      pollDetails?.description ||
      routerState?.description ||
      '',
    totalEligible:
      votingStats?.totalEligible ??
      votingStats?.totalMembers ??
      totalEligible ??
      pollDetails?.totalEligible ??
      routerState?.totalEligible ??
      0,
    totalVoted:
      votingStats?.votedCount ??
      votingStats?.votedMembers ??
      totalVoted ??
      pollDetails?.totalVoted ??
      routerState?.totalVoted ??
      0,
    totalNonVoted:
      votingStats?.notVotedCount ??
      votingStats?.notVotedMembers ??
      totalNonVoted ??
      pollDetails?.totalNonVoted ??
      routerState?.totalNonVoted ??
      0,
  };

  // --- STATE MANAGEMENT ---
  const [activeTab, setActiveTab] = useState('nonVoted'); // 'nonVoted' | 'voted' | 'total'

  // Table filters & pagination
  const [searchText, setSearchText] = useState('');
  const [selectedState, setSelectedState] = useState('All States');
  const [selectedDistrict, setSelectedDistrict] = useState('All Districts');
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(15);

  // --- ROLE SCOPING ---
  const isAdmin = localStorage.getItem('designation') === 'admin';
  const isStateHead = !isAdmin && (localStorage.getItem('isStateHead') === 'true' || localStorage.getItem('isStateHead') === true);
  const isDistrictHead = !isAdmin && !isStateHead && (localStorage.getItem('isDistrictHead') === 'true' || localStorage.getItem('isDistrictHead') === true);

  const [columnView, setColumnView] = useState(isStateHead ? 'District' : 'State'); // 'State' | 'District'
  const [linkCopied, setLinkCopied] = useState(false);

  // Exact DOM container reference & measurement for zero-scroll full viewport lock
  const containerRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(
    typeof window !== 'undefined' ? Math.max(380, window.innerHeight - 130) : 550
  );

  useEffect(() => {
    // Lock document.body overflow so browser window scrollbar is strictly disabled
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const updateHeight = () => {
      if (containerRef.current) {
        const measured = containerRef.current.clientHeight;
        if (measured > 100) {
          setContentHeight(measured);
        }
      }
    };

    updateHeight();

    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      observer = new ResizeObserver(() => {
        updateHeight();
      });
      observer.observe(containerRef.current);
    }

    window.addEventListener('resize', updateHeight);

    return () => {
      document.body.style.overflow = originalOverflow;
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Available height for left cards: contentHeight minus 1 gap of 0.35rem (≈ 6px)
  const totalCardsHeight = Math.max(300, contentHeight - 6);
  // Row 1 & Row 2 heights:
  // When District Head: two stacked cards (Pie on top, QR on bottom) sharing total height (~48% and ~52%)
  // When Admin/State Head: Row 1 gets ~44% (side-by-side Pie & QR) and Row 2 gets ~56% (Column Chart)
  const row1Height = isDistrictHead
    ? Math.floor(totalCardsHeight * 0.48)
    : Math.floor(totalCardsHeight * 0.44);
  const row2Height = totalCardsHeight - row1Height;

  const pieChartHeight = Math.max(120, row1Height - 32);
  const columnChartHeight = Math.max(130, row2Height - 36);
  const qrCodeSize = isDistrictHead
    ? Math.max(105, Math.min(145, row2Height - 125))
    : Math.max(95, Math.min(160, row1Height - 74));

  const votingUrl = `${window.location.origin}/castpoll/id/${id || ''}`;
  const selectedLogo = logo === 'WORK' ? logoBase64 : compLogoBase64;

  // --- GLOBAL LOCATION OPTIONS (LOADED FROM DB APIS SCOPED TO USER ROLE) ---
  const [allAvailableStates, setAllAvailableStates] = useState([]);
  const [availableDistrictsForState, setAvailableDistrictsForState] = useState([]);

  // Fetch real states scoped to user role from DB on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const options = {
      url: fetchStatesForHead,
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    };
    HttpService(options)
      .then((resp) => {
        const fetched = resp?.data?.data || [];
        if (Array.isArray(fetched)) {
          const names = fetched
            .map((item) => (typeof item === 'string' ? item : item.name || item.state || item.stateName))
            .filter(Boolean);
          const uniqueStates = Array.from(new Set(names)).sort();
          setAllAvailableStates(uniqueStates);
          if (!isAdmin && uniqueStates.length > 0) {
            setSelectedState(uniqueStates[0]);
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching states for head:', err);
      });
  }, [isAdmin]);

  // Fetch real districts scoped to user role when state changes
  useEffect(() => {
    if (selectedState && selectedState !== 'All States') {
      const token = localStorage.getItem('token');
      const options = {
        url: fetchDistrictsForHead,
        method: 'GET',
        params: { stateFilter: selectedState },
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      };
      HttpService(options)
        .then((resp) => {
          const fetched = resp?.data?.data || [];
          if (Array.isArray(fetched)) {
            const names = fetched
              .map((item) => (typeof item === 'string' ? item : item.name || item.district || item.districtName))
              .filter(Boolean);
            const uniqueDistricts = Array.from(new Set(names)).sort();
            setAvailableDistrictsForState(uniqueDistricts);
            if (isDistrictHead && uniqueDistricts.length > 0) {
              setSelectedDistrict(uniqueDistricts[0]);
            }
          }
        })
        .catch((err) => {
          console.error('Error fetching districts for head:', err);
        });
    } else {
      setAvailableDistrictsForState([]);
    }
  }, [selectedState, isDistrictHead]);

  const defaultState = useMemo(() => {
    if (!isAdmin && allAvailableStates.length === 1) {
      return allAvailableStates[0];
    }
    return 'All States';
  }, [isAdmin, allAvailableStates]);

  const defaultDistrict = useMemo(() => {
    if (isDistrictHead && availableDistrictsForState.length === 1) {
      return availableDistrictsForState[0];
    }
    return 'All Districts';
  }, [isDistrictHead, availableDistrictsForState]);

  const stateOptions = useMemo(() => {
    const list = allAvailableStates.map((s) => ({ name: s }));
    if (isAdmin || list.length > 1) {
      return [{ name: 'All States' }, ...list];
    }
    if (list.length === 0) {
      return selectedState ? [{ name: selectedState }] : [];
    }
    return list;
  }, [allAvailableStates, isAdmin, selectedState]);

  const districtOptions = useMemo(() => {
    const sorted = Array.from(new Set(availableDistrictsForState)).filter(Boolean).sort();
    const list = sorted.map((d) => ({ name: d }));
    if (isAdmin || isStateHead || list.length > 1) {
      return [{ name: 'All Districts' }, ...list];
    }
    if (list.length === 0) {
      return selectedDistrict ? [{ name: selectedDistrict }] : [];
    }
    return list;
  }, [availableDistrictsForState, isAdmin, isStateHead, selectedDistrict]);

  const isLocationFilterDirty = useMemo(() => {
    const stateDirty = selectedState !== defaultState && (isAdmin || allAvailableStates.length > 1);
    const districtDirty = selectedDistrict !== defaultDistrict && (isAdmin || isStateHead || availableDistrictsForState.length > 1);
    return stateDirty || districtDirty;
  }, [selectedState, defaultState, isAdmin, allAvailableStates.length, selectedDistrict, defaultDistrict, isStateHead, availableDistrictsForState.length]);

  // Tab counts based on global location filters and API voting statistics
  const { nonVotedCount, votedCount, totalMembersCount } = useMemo(() => {
    if (votingStats) {
      return {
        nonVotedCount: votingStats.notVotedCount ?? votingStats.notVotedMembers ?? 0,
        votedCount: votingStats.votedCount ?? votingStats.votedMembers ?? 0,
        totalMembersCount: votingStats.totalEligible ?? votingStats.totalMembers ?? 0,
      };
    }
    if (selectedState !== 'All States' && stateVotingStats?.data && Array.isArray(stateVotingStats.data)) {
      const stateMatches = stateVotingStats.data.filter((item) => {
        const cleanState = (item.state || item.stateDisplayName || item.name || '').replace(/\s*\((Male|Female)\)/i, '').trim();
        return cleanState === selectedState || item.state === selectedState;
      });
      if (stateMatches.length > 0) {
        const nv = stateMatches.reduce((acc, curr) => acc + (curr.notVotedMembers || 0), 0);
        const v = stateMatches.reduce((acc, curr) => acc + (curr.votedMembers || 0), 0);
        const t = stateMatches.reduce((acc, curr) => acc + (curr.totalMembers || 0), 0);
        return { nonVotedCount: nv, votedCount: v, totalMembersCount: t };
      }
    }
    if (selectedDistrict !== 'All Districts' && districtVotingStats?.data && Array.isArray(districtVotingStats.data)) {
      const districtMatches = districtVotingStats.data.filter((item) => {
        const cleanDistrict = (item.district || item.districtDisplayName || item.name || '').replace(/\s*\((Male|Female)\)/i, '').trim();
        return cleanDistrict === selectedDistrict || item.district === selectedDistrict;
      });
      if (districtMatches.length > 0) {
        const nv = districtMatches.reduce((acc, curr) => acc + (curr.notVotedMembers || 0), 0);
        const v = districtMatches.reduce((acc, curr) => acc + (curr.votedMembers || 0), 0);
        const t = districtMatches.reduce((acc, curr) => acc + (curr.totalMembers || 0), 0);
        return { nonVotedCount: nv, votedCount: v, totalMembersCount: t };
      }
    }
    if (membersTableData) {
      return {
        nonVotedCount: activeTab === 'nonVoted' ? (membersTableData.totalRecords || 0) : 0,
        votedCount: activeTab === 'voted' ? (membersTableData.totalRecords || 0) : 0,
        totalMembersCount: membersTableData.totalRecords || 0,
      };
    }
    return {
      nonVotedCount: 0,
      votedCount: 0,
      totalMembersCount: 0,
    };
  }, [votingStats, selectedState, selectedDistrict, stateVotingStats, districtVotingStats, membersTableData, activeTab]);

  // Dynamic Pie Data reacting to global location filters and API voting statistics
  const currentPieData = useMemo(() => {
    return [
      { name: 'Voted', y: votedCount, color: '#2e7d32' },
      { name: 'Non-Voted', y: nonVotedCount, color: orange },
    ];
  }, [votedCount, nonVotedCount]);

  // --- PIE CHART CONFIGURATION ---
  const pieChartOptions = useMemo(() => {
    const total = currentPieData.reduce((acc, p) => acc + p.y, 0) || 1;
    return {
      chart: {
        type: 'pie',
        height: pieChartHeight,
        backgroundColor: 'transparent',
        spacingTop: 2,
        spacingBottom: 2,
        spacingLeft: 6,
        spacingRight: 6,
        reflow: true,
      },
      title: {
        text: '',
      },
      credits: { enabled: false },
      plotOptions: {
        pie: {
          size: '68%',
          innerSize: '50%',
          center: isDistrictHead ? ['44%', '50%'] : ['52%', '50%'],
          showInLegend: true,
          dataLabels: {
            enabled: true,
            distance: -12,
            format: '{point.percentage:.0f}%',
            style: { fontSize: '0.72rem', fontWeight: 700, color: '#ffffff', textOutline: 'none' },
          },
        },
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b>: <b>{point.y}</b> members ({point.percentage:.1f}%)',
      },
      legend: {
        enabled: true,
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
        itemMarginTop: 4,
        itemMarginBottom: 4,
        itemStyle: { fontSize: '0.74rem', fontWeight: '600', color: headingBlue },
        symbolRadius: 4,
        labelFormatter: function () {
          const percent = this.percentage !== undefined ? this.percentage.toFixed(0) : ((this.y / total) * 100).toFixed(0);
          return `${this.name}: ${this.y} (${percent}%)`;
        },
      },
      series: [
        {
          name: 'Voting Statistics',
          colorByPoint: true,
          data: currentPieData,
        },
      ],
    };
  }, [currentPieData, pieChartHeight, isDistrictHead]);

  // Trigger overall / pie chart voting statistics fetch reacting to location filters
  useEffect(() => {
    if (id) {
      fetchVotingStatistics(id, selectedState, selectedDistrict);
    }
  }, [id, selectedState, selectedDistrict]);

  // --- COLUMN CHART DATA & SET PAGINATION ---
  const [chartPage, setChartPage] = useState(1);
  const isStateApiData = columnView === 'State' && Boolean(stateVotingStats?.data);
  const isDistrictApiData = (columnView === 'District' || columnView === 'District Head') && Boolean(districtVotingStats?.data);
  const chartPageSize = 15; // Offset set to 15 per set/page for column chart

  // Reset chart page whenever view or filters change
  useEffect(() => {
    setChartPage(1);
  }, [columnView, selectedState, selectedDistrict]);

  // Trigger state-wise poll voting statistics API fetch when viewing State data (only for Admin)
  useEffect(() => {
    if (id && columnView === 'State' && !isStateHead && !isDistrictHead) {
      fetchStateVotingStatistics(id, chartPage, chartPageSize, selectedState);
    }
  }, [id, columnView, chartPage, selectedState, chartPageSize, isStateHead, isDistrictHead]);

  // Trigger district-wise poll voting statistics API fetch when viewing District data (not for District Head)
  useEffect(() => {
    if (id && (columnView === 'District' || columnView === 'District Head') && !isDistrictHead) {
      fetchDistrictVotingStatistics(id, chartPage, chartPageSize, selectedState, selectedDistrict);
    }
  }, [id, columnView, chartPage, selectedState, selectedDistrict, chartPageSize, isDistrictHead]);

  const totalColumnItems = useMemo(() => {
    if (isStateApiData) {
      return stateVotingStats.totalRecords ?? stateVotingStats.totalStates ?? stateVotingStats.data.length;
    }
    if (isDistrictApiData) {
      return districtVotingStats.totalRecords ?? districtVotingStats.totalDistricts ?? districtVotingStats.data.length;
    }
    return 0;
  }, [isStateApiData, isDistrictApiData, stateVotingStats, districtVotingStats]);

  const totalChartPages = useMemo(() => {
    if (isStateApiData && stateVotingStats.totalPages) {
      return stateVotingStats.totalPages;
    }
    if (isDistrictApiData && districtVotingStats.totalPages) {
      return districtVotingStats.totalPages;
    }
    return Math.ceil(totalColumnItems / chartPageSize) || 1;
  }, [isStateApiData, isDistrictApiData, stateVotingStats, districtVotingStats, totalColumnItems, chartPageSize]);

  const paginatedColumnData = useMemo(() => {
    if (isStateApiData && Array.isArray(stateVotingStats.data)) {
      return stateVotingStats.data;
    }
    if (isDistrictApiData && Array.isArray(districtVotingStats.data)) {
      return districtVotingStats.data;
    }
    return [];
  }, [isStateApiData, isDistrictApiData, stateVotingStats, districtVotingStats]);

  const totalColumnVotes = useMemo(() => {
    if (columnView === 'State' && isStateApiData) {
      return stateVotingStats.data.reduce((acc, item) => acc + (item.votedMembers || 0), 0);
    }
    if (isDistrictApiData && Array.isArray(districtVotingStats.data)) {
      return districtVotingStats.data.reduce((acc, item) => acc + (item.votedMembers || 0), 0);
    }
    return 0;
  }, [columnView, isStateApiData, stateVotingStats, isDistrictApiData, districtVotingStats]);

  const startRecord = totalColumnItems === 0 ? 0 : (chartPage - 1) * chartPageSize + 1;
  const endRecord = Math.min(chartPage * chartPageSize, totalColumnItems);

  // --- COLUMN CHART CONFIGURATION (STATE OR DISTRICT TOGGLE, DESCENDING ORDER) ---
  const columnChartOptions = useMemo(() => {
    const isStateApi = columnView === 'State' && Boolean(stateVotingStats?.data);
    const isDistrictApi = (columnView === 'District' || columnView === 'District Head') && Boolean(districtVotingStats?.data);
    const isApi = isStateApi || isDistrictApi;
    const categories = paginatedColumnData.map((d) => d.name || d.districtDisplayName || d.stateDisplayName || d.district || d.state);
    const seriesData = paginatedColumnData.map((d) => {
      const isFemale =
        (d.gender && d.gender.toLowerCase() === 'female') ||
        /\(female\)/i.test(d.name || d.districtDisplayName || d.stateDisplayName || d.district || d.state || '');
      const isMale =
        (d.gender && d.gender.toLowerCase() === 'male') ||
        /\(male\)/i.test(d.name || d.districtDisplayName || d.stateDisplayName || d.district || d.state || '');
      const pointColor = isFemale ? pink : skyBlue;

      return {
        name: d.name || d.districtDisplayName || d.district || d.stateDisplayName || d.state,
        y: d.y !== undefined ? d.y : (d.notVotedPercentage !== undefined ? d.notVotedPercentage : (d.votes || 0)),
        color: pointColor,
        votedMembers: d.votedMembers ?? d.votes ?? 0,
        notVotedMembers: d.notVotedMembers ?? 0,
        totalMembers: d.totalMembers ?? d.votes ?? 0,
        votedPercentage: d.votedPercentage ?? 0,
        notVotedPercentage: d.notVotedPercentage ?? d.y ?? 0,
        stateHead: d.stateHead,
        stateHeadCN: d.stateHeadCN,
        districtHead: d.districtHead,
        districtHeadCN: d.districtHeadCN,
        district: d.district,
        state: d.state,
        gender: d.gender || (isFemale ? 'Female' : 'Male'),
      };
    });

    return {
      chart: {
        type: 'column',
        height: columnChartHeight,
        backgroundColor: 'transparent',
        spacingTop: 28,
        spacingBottom: 22,
        spacingLeft: 8,
        spacingRight: 8,
        reflow: true,
      },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: {
        categories: categories,
        crosshair: true,
        labels: {
          rotation: -40,
          align: 'right',
          reserveSpace: true,
          style: { fontSize: '0.62rem', color: headingBlue, fontWeight: '500' },
        },
      },
      yAxis: {
        min: 0,
        max: isApi ? 115 : undefined,
        maxPadding: isApi ? 0.2 : 0.18,
        showLastLabel: true,
        title: {
          text: isApi ? 'Not Voted (%)' : 'Votes',
          style: { color: headingBlue, fontSize: '0.7rem', fontWeight: '500' },
        },
        labels: {
          format: isApi ? '{value}%' : '{value}',
          style: { color: headingBlue, fontSize: '0.68rem' },
        },
      },
      plotOptions: {
        column: {
          borderRadius: 2,
          maxPointWidth: 24,
          pointPadding: 0.04,
          groupPadding: 0.06,
          dataLabels: {
            enabled: true,
            format: isApi ? '{point.y:.1f}%' : '{point.y}',
            style: { fontWeight: '600', fontSize: '0.62rem', color: headingBlue },
            crop: false,
            overflow: 'allow',
            y: -2,
          },
        },
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const p = this.point;
          if (isApi || p.totalMembers !== undefined) {
            return `
              <div style="font-size: 0.74rem; padding: 4px; line-height: 1.45;">
                <b style="color: ${p.color || headingBlue}; font-size: 0.8rem;">${p.name}</b><br/>
                <span style="color: ${maroon};">● Not Voted: <b>${p.notVotedMembers ?? 0} (${p.notVotedPercentage ?? p.y}%)</b></span><br/>
                <span style="color: ${green};">● Voted: <b>${p.votedMembers ?? 0} (${p.votedPercentage ?? 0}%)</b></span><br/>
                <span>Total Members: <b>${p.totalMembers}</b></span>
                ${p.gender ? `<br/><span style="color: #666; font-size: 0.68rem;">Gender: <b style="color: ${p.color};">${p.gender}</b></span>` : ''}
                ${p.districtHead ? `<br/><span style="color: #666; font-size: 0.68rem;">District Head: <b>${p.districtHead}</b> (${p.districtHeadCN || 'N/A'})</span>` : ''}
                ${p.stateHead ? `<br/><span style="color: #666; font-size: 0.68rem;">State Head: <b>${p.stateHead}</b> (${p.stateHeadCN || 'N/A'})</span>` : ''}
                ${p.state ? `<br/><span style="color: #666; font-size: 0.68rem;">State: ${p.state}</span>` : ''}
              </div>
            `;
          }
          return `<b>${this.x}</b><br/><b>${this.y} votes</b> cast`;
        },
      },
      legend: { enabled: false },
      series: [
        {
          name: isApi ? 'Not Voted %' : 'Votes',
          data: seriesData,
        },
      ],
    };
  }, [paginatedColumnData, columnView, columnChartHeight, stateVotingStats, districtVotingStats]);

  // Trigger fetch for poll voting members table with search debounce
  useEffect(() => {
    if (id) {
      const delay = searchText ? 350 : 0;
      const timer = setTimeout(() => {
        fetchPollVotingMembersData(
          id,
          page,
          offset,
          activeTab,
          selectedState,
          selectedDistrict,
          searchText
        );
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [id, page, offset, activeTab, selectedState, selectedDistrict, searchText]);

  // Server-Side API Members Data
  const isApiMembers = Boolean(membersTableData && Array.isArray(membersTableData.data));
  const totalRecords = isApiMembers ? (membersTableData.totalRecords || 0) : 0;
  const totalPages = isApiMembers
    ? (membersTableData.totalPages || (totalRecords > 0 ? 1 : 0))
    : 1;

  const paginatedMembers = useMemo(() => {
    if (isApiMembers) {
      return membersTableData.data;
    }
    return [];
  }, [isApiMembers, membersTableData]);

  // --- EXCEL EXPORT ---
  const handleExportExcel = () => {
    const listToExport = isApiMembers ? paginatedMembers : [];
    if (!listToExport || listToExport.length === 0) {
      dispatch(showSnackBar({ message: 'No records available to export.', severity: 'info' }));
      return;
    }
    const exportData = listToExport.map((m, index) => ({
      'S.No': (page - 1) * offset + index + 1,
      'Member Name': m.name || '-',
      'Contact Number': m.contactNumber || '-',
      State: m.state || '-',
      District: m.district || '-',
      'Voting Status': (m.hasVoted ?? m.voted ?? (m.status === 'voted')) ? 'Voted' : 'Not Voted',
      'District Head': m.districtHeadName || 'N/A',
      'District Head Contact': m.districtHeadCN || m.districtHeadContact || 'N/A',
      'State Head': m.stateHeadName || 'N/A',
      'State Head Contact': m.stateHeadCN || m.stateHeadContact || 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Poll Voters');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(dataBlob, `Poll_Voters_Report_${activeTab}.xlsx`);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(votingUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngFile;
      downloadLink.download = 'Poll_Voting_QR.png';
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const renderPieCard = () => (
    <Paper style={{ ...paperCardStyle, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', p: '0.15rem 0.25rem' }}>
      {/* Header Box */}
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.15rem 0.5rem', borderBottom: '1px solid #D9D9D9', flexShrink: 0 }}>
        <Typography style={{ color: headingBlue, fontSize: '0.86rem', fontWeight: '600' }}>
          Voting Statistics
        </Typography>
        {votingStats && (
          <Typography style={{ fontSize: '0.72rem', color: headingBlue, fontWeight: '500' }}>
            Total: <strong>{votingStats.totalEligible ?? votingStats.totalMembers}</strong> ({votingStats.votedPercentage}% Voted)
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', overflow: 'hidden' }}>
        {isLoading ? (
          <CircularProgress size={30} style={{ color: headingBlue }} />
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={pieChartOptions}
            containerProps={{ style: { width: '100%', height: '100%' } }}
          />
        )}
      </Box>
    </Paper>
  );

  const renderQrCard = () => (
    <Paper style={{ ...paperCardStyle, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', p: '0.15rem 0.25rem' }}>
      {/* Header Box */}
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.15rem 0.5rem', borderBottom: '1px solid #D9D9D9', flexShrink: 0 }}>
        <Typography style={{ color: headingBlue, fontSize: '0.86rem', fontWeight: '600' }}>
          Scan to Vote
        </Typography>
        <Tooltip title="Download QR (PNG)">
          <IconButton size="small" onClick={handleDownloadQR} style={{ color: headingBlue, padding: '0.05rem' }}>
            <Download sx={{ fontSize: '1.05rem' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* QR Code Body */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: '0.2rem',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          ref={qrRef}
          sx={{
            p: '0.25rem',
            border: '1px solid #D9D9D9',
            backgroundColor: pureWhite,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <QRCodeSVG
            value={votingUrl}
            size={qrCodeSize}
            fgColor={headingBlue}
            imageSettings={{
              src: selectedLogo,
              height: Math.round(qrCodeSize * 0.18),
              width: Math.round(qrCodeSize * 0.28),
              excavate: true,
            }}
          />
        </Box>

        {/* Link Box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '280px',
            backgroundColor: '#f5f5f5',
            padding: '0.1rem 0.4rem',
            border: '1px solid #D9D9D9',
            borderRadius: '3px',
            mt: '0.12rem',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.68rem',
              color: headingBlue,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '230px',
            }}
          >
            {votingUrl}
          </Typography>
          <Tooltip title={linkCopied ? 'Copied!' : 'Copy Link'}>
            <IconButton size="small" onClick={handleCopyLink} sx={{ color: headingBlue, p: '0.05rem' }}>
              <ContentCopy sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography sx={{ fontSize: '0.65rem', color: '#666', mt: '0.1rem', textAlign: 'center' }}>
          Scan with mobile camera to cast vote
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <>
      {(isLoading || isStateStatsLoading || isDistrictStatsLoading || isMembersLoading) && <Loader />}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: '#fff',
          pt: '0.2rem',
          px: '0.5rem',
          pb: '0.5rem',
        }}
      >
        {/* ============================================================== */}
        {/* 1. TOP DETAILS BAR: POLL DETAILS & ACTION BUTTONS              */}
        {/* ============================================================== */}
        <Box style={{ ...topBarBoxStyle, flexShrink: 0, padding: '0.3rem 0.6rem' }}>
          {/* Left: Back button & Poll Details in a single compact row */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
            <IconButton
              onClick={onBack || (() => navigate(-1))}
              size="small"
              style={{ color: headingBlue, padding: '0.15rem' }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>

            <Box style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <Typography style={{ fontSize: '0.94rem', fontWeight: '700', color: headingBlue }}>
                {activePollDetails.programName}
              </Typography>
              {activePollDetails.status && (
                <Chip
                  label={activePollDetails.status}
                  size="small"
                  style={{
                    backgroundColor: Donegreen,
                    color: green,
                    fontWeight: '600',
                    fontSize: '0.68rem',
                    height: '1.2rem',
                    borderRadius: '3px',
                  }}
                />
              )}
              {activePollDetails.WORKerCategory && (
                <Chip
                  label={formatCategoryDisplay(activePollDetails.WORKerCategory)}
                  size="small"
                  style={{
                    backgroundColor: lightBlue,
                    color: headingBlue,
                    fontWeight: '600',
                    fontSize: '0.68rem',
                    height: '1.2rem',
                    borderRadius: '3px',
                  }}
                />
              )}
              {(activePollDetails.createdDate || activePollDetails.expiryDate) && (
                <Typography style={{ fontSize: '0.74rem', color: '#666', marginLeft: '0.2rem' }}>
                  Active: <strong>{activePollDetails.createdDate}</strong>
                  {activePollDetails.expiryDate && (
                    <> - <strong>{activePollDetails.expiryDate}</strong></>
                  )}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right: Global Location Filters & Actions */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
            <CustomDropDown
              list={stateOptions}
              value={selectedState}
              placeHolder={defaultState}
              setFunc={(val) => {
                setSelectedState(val);
                setSelectedDistrict('All Districts');
                setPage(1);
              }}
              width="8.8rem"
              height="1.85rem"
              regular={true}
            />

            <CustomDropDown
              list={districtOptions}
              value={selectedDistrict}
              placeHolder={defaultDistrict}
              setFunc={(val) => {
                setSelectedDistrict(val);
                setPage(1);
              }}
              width="9.2rem"
              height="1.85rem"
              regular={true}
            />

            {isLocationFilterDirty && (
              <Tooltip title="Reset Location Filters">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedState(defaultState);
                    setSelectedDistrict(defaultDistrict);
                    setPage(1);
                  }}
                  style={{ color: maroon, padding: '0.15rem' }}
                >
                  <RestartAlt fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Button
              style={{ ...buttonStyle, height: '1.85rem', padding: '0.2rem 0.5rem', fontSize: '0.78rem' }}
              size="small"
              onClick={handleExportExcel}
              startIcon={<FileDownload style={{ fontSize: '0.9rem' }} />}
            >
              Export Excel
            </Button>
          </Box>
        </Box>

        {/* ============================================================== */}
        {/* 2. MAIN DASHBOARD CONTENT: CHARTS ON LEFT (60%), TABLE ON RIGHT (40%) */}
        {/* ============================================================== */}
        <Box
          ref={containerRef}
          sx={{
            display: 'flex',
            gap: '0.4rem',
            mt: '0.25rem',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* LEFT COLUMN: WHEN DISTRICT HEAD (35% WIDTH, STACKED PIE & QR), ADMIN/STATE HEAD (60% WIDTH, SIDE-BY-SIDE + COLUMN CHART) */}
          <Box
            sx={{
              flex: isDistrictHead ? '0 0 calc(35% - 0.2rem)' : '0 0 calc(60% - 0.2rem)',
              width: isDistrictHead ? 'calc(35% - 0.2rem)' : 'calc(60% - 0.2rem)',
              maxWidth: isDistrictHead ? 'calc(35% - 0.2rem)' : 'calc(60% - 0.2rem)',
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem',
              overflow: 'hidden',
            }}
          >
            {isDistrictHead ? (
              <>
                {/* DISTRICT HEAD: TOP CARD = PIE CHART */}
                <Box sx={{ height: row1Height, minHeight: 0, flexShrink: 0, width: '100%' }}>
                  {renderPieCard()}
                </Box>

                {/* DISTRICT HEAD: BOTTOM CARD = QR CODE SCANNER */}
                <Box sx={{ height: row2Height, minHeight: 0, flexShrink: 0, width: '100%' }}>
                  {renderQrCard()}
                </Box>
              </>
            ) : (
              <>
                {/* ROW 1: SCANNER (7/12) & PIE (5/12) SIDE BY SIDE */}
                <Box sx={{ height: row1Height, minHeight: 0, flexShrink: 0, display: 'flex', gap: '0.35rem' }}>
                  <Box sx={{ flex: 5, height: '100%', minWidth: 0 }}>
                    {renderPieCard()}
                  </Box>
                  <Box sx={{ flex: 7, height: '100%', minWidth: 0 }}>
                    {renderQrCard()}
                  </Box>
                </Box>

                {/* ROW 2: UNIFIED COLUMN CHART CARD WITH STATE / DISTRICT TOGGLE */}
                <Box sx={{ height: row2Height, minHeight: 0, flexShrink: 0 }}>
                  <Paper style={{ ...paperCardStyle, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', p: '0.15rem 0.25rem' }}>
                    {/* Header Box with Title, Total Counts, State/District Toggle, and Leads-Style Pagination */}
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.22rem 0.5rem',
                        borderBottom: '1px solid #D9D9D9',
                        flexShrink: 0,
                        gap: '0.35rem',
                      }}
                    >
                      {/* Left: Title & Total Counts Chip & Gender Indicators */}
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                        <Typography style={{ color: headingBlue, fontSize: '0.86rem', fontWeight: '600' }}>
                          {(columnView === 'District' || columnView === 'District Head') ? 'District-wise Voting' : 'State-wise Voting'}
                        </Typography>
                        <Chip
                          label={`Total: ${totalColumnItems} ${(columnView === 'District' || columnView === 'District Head') ? 'Districts' : 'States'} (${totalColumnVotes} Votes)`}
                          size="small"
                          style={{
                            backgroundColor: lightBlue,
                            color: headingBlue,
                            fontWeight: '600',
                            fontSize: '0.68rem',
                            height: '1.25rem',
                            borderRadius: '3px',
                          }}
                        />
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: '0.15rem' }}>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: '0.18rem' }}>
                            <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: skyBlue }} />
                            <Typography style={{ fontSize: '0.68rem', color: headingBlue, fontWeight: 600 }}>Male</Typography>
                          </Box>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: '0.18rem' }}>
                            <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: pink }} />
                            <Typography style={{ fontSize: '0.68rem', color: pink, fontWeight: 600 }}>Female</Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Right: Toggle & Leads-Style Pagination Controls */}
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                        {!isStateHead && (
                          <RadioGroup
                            row
                            value={columnView}
                            onChange={(e) => {
                              setColumnView(e.target.value);
                              setChartPage(1);
                            }}
                            sx={{ gap: '0.5rem', my: 0 }}
                          >
                            <FormControlLabel
                              value="State"
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    p: 0,
                                    mr: '0.2rem',
                                    color: headingBlue,
                                    '&.Mui-checked': { color: headingBlue },
                                  }}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '0.74rem', fontWeight: columnView === 'State' ? 700 : 500, color: headingBlue }}>
                                  State
                                </Typography>
                              }
                              sx={{ m: 0 }}
                            />
                            <FormControlLabel
                              value="District"
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    p: 0,
                                    mr: '0.2rem',
                                    color: headingBlue,
                                    '&.Mui-checked': { color: headingBlue },
                                  }}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '0.74rem', fontWeight: (columnView === 'District' || columnView === 'District Head') ? 700 : 500, color: headingBlue }}>
                                  District Head
                                </Typography>
                              }
                              sx={{ m: 0 }}
                            />
                          </RadioGroup>
                        )}

                        {/* Leads-Style Pagination Option on Top Right */}
                        {totalColumnItems > 0 && (
                          <Box style={{ display: 'flex', alignItems: 'center', gap: '0.15rem', marginLeft: '0.15rem' }}>
                            <Typography style={{ color: maroon, fontSize: '0.74rem', marginRight: '0.15rem' }}>
                              {startRecord}-{endRecord} of {totalColumnItems}
                            </Typography>

                            <IconButton
                              size="small"
                              onClick={() => setChartPage((p) => Math.max(1, p - 1))}
                              disabled={chartPage === 1}
                              sx={{ p: '0.1rem' }}
                            >
                              <KeyboardArrowLeft
                                size="small"
                                sx={{ fontSize: '1.05rem', color: chartPage === 1 ? mediumGrey : headingBlue }}
                              />
                            </IconButton>

                            <Button
                              variant="contained"
                              style={{
                                minWidth: '0rem',
                                width: '1.35rem',
                                height: '1.35rem',
                                borderRadius: '50%',
                                backgroundColor: headingBlue,
                                color: 'white',
                                padding: '0rem',
                                fontSize: '0.72rem',
                                alignItems: 'center',
                              }}
                            >
                              {chartPage}
                            </Button>

                            <IconButton
                              size="small"
                              onClick={() => setChartPage((p) => Math.min(totalChartPages, p + 1))}
                              disabled={chartPage >= totalChartPages || totalChartPages === 0}
                              sx={{ p: '0.1rem' }}
                            >
                              <KeyboardArrowRight
                                size="small"
                                sx={{ fontSize: '1.05rem', color: (chartPage >= totalChartPages || totalChartPages === 0) ? mediumGrey : headingBlue }}
                              />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Column Chart Container */}
                    <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden', mt: '0.2rem' }}>
                      {(isStateStatsLoading || isDistrictStatsLoading) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <CircularProgress size={30} style={{ color: headingBlue }} />
                        </Box>
                      ) : paginatedColumnData.length === 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <Typography sx={{ color: mediumGrey, fontSize: '0.82rem' }}>
                            No voting statistics found for this selection.
                          </Typography>
                        </Box>
                      ) : (
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={columnChartOptions}
                          containerProps={{ style: { width: '100%', height: '100%' } }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Box>
              </>
            )}
          </Box>

          {/* RIGHT COLUMN: WHEN DISTRICT HEAD (65% WIDTH), ADMIN/STATE HEAD (40% WIDTH) */}
          <Box
            sx={{
              flex: isDistrictHead ? '0 0 calc(65% - 0.2rem)' : '0 0 calc(40% - 0.2rem)',
              width: isDistrictHead ? 'calc(65% - 0.2rem)' : 'calc(40% - 0.2rem)',
              maxWidth: isDistrictHead ? 'calc(65% - 0.2rem)' : 'calc(40% - 0.2rem)',
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Paper style={{ ...paperCardStyle, width: '100%', height: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', p: '0.15rem 0.25rem', overflow: 'hidden' }}>
              {/* Single-line Table Header: Search Bar & Tabs (Not Voted, Voted, Total) */}
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.15rem 0.4rem',
                  borderBottom: '1px solid #D9D9D9',
                  flexWrap: 'nowrap',
                  flexShrink: 0,
                  gap: '0.2rem',
                }}
              >
                {/* Left: Not Voted, Voted, Total Tabs (Options) */}
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, val) => {
                      setActiveTab(val);
                      setPage(1);
                    }}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                      minHeight: 30,
                      '& .MuiTab-root': {
                        textTransform: 'capitalize',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        minHeight: 30,
                        py: 0.1,
                        px: 0.7,
                        color: '#666',
                        '&.Mui-selected': { color: headingBlue },
                      },
                    }}
                  >
                    <Tab
                      value="nonVoted"
                      label={
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span>Not Voted</span>
                          {/* <Chip
                              label={nonVotedCount}
                              size="small"
                              style={{
                                backgroundColor: lightRed,
                                color: maroon,
                                fontSize: '0.68rem',
                                fontWeight: '700',
                                height: '1.1rem',
                                borderRadius: '3px',
                              }}
                            /> */}
                        </Box>
                      }
                    />
                    <Tab
                      value="voted"
                      label={
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span>Voted</span>
                          {/* <Chip
                            label={votedCount}
                            size="small"
                            style={{
                              backgroundColor: Donegreen,
                              color: green,
                              fontSize: '0.68rem',
                              fontWeight: '700',
                              height: '1.1rem',
                              borderRadius: '3px',
                            }}
                          /> */}
                        </Box>
                      }
                    />
                    <Tab
                      value="total"
                      label={
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span>Total</span>
                          {/* <Chip
                            label={totalMembersCount}
                            size="small"
                            style={{
                              backgroundColor: '#eaeaea',
                              color: headingBlue,
                              fontSize: '0.68rem',
                              fontWeight: '700',
                              height: '1.1rem',
                              borderRadius: '3px',
                            }}
                          /> */}
                        </Box>
                      }
                    />
                  </Tabs>
                </Box>

                {/* Right: Search Bar */}
                <Box style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                  <CustomTextField
                    placeHolder="Search member..."
                    value={searchText}
                    setFunc={(val) => {
                      setSearchText(val);
                      setPage(1);
                    }}
                    width="10rem"
                  />
                  {searchText && (
                    <Tooltip title="Clear Search">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchText('');
                          setPage(1);
                        }}
                        style={{ color: maroon, padding: '0.15rem' }}
                      >
                        <RestartAlt fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* Table Area */}
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  border: '1px solid #D9D9D9',
                  mt: 0.2,
                  overflowY: 'auto',
                  ...scrollbarStyle,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ ...headerCellStyle, width: '28%' }}>Member Details</TableCell>
                      <TableCell style={{ ...headerCellStyle, width: '20%' }}>Location</TableCell>
                      <TableCell style={{ ...headerCellStyle, width: '22%' }}>District Head</TableCell>
                      <TableCell style={{ ...headerCellStyle, width: '22%' }}>State Head</TableCell>
                      <TableCell style={{ ...headerCellStyle, width: '8%' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody style={{ textAlign: 'center' }}>
                    {paginatedMembers.length > 0 ? (
                      paginatedMembers.map((member, index) => {
                        const hasVoted =
                          member.hasVoted !== undefined
                            ? member.hasVoted
                            : member.status === 'voted' || Boolean(member.voted);
                        const distContact = member.districtHeadCN || member.districtHeadContact;
                        const stateContact = member.stateHeadCN || member.stateHeadContact;

                        return (
                          <TableRow key={member._id || member.id || index} hover>
                            {/* 1. Member Name & Contact */}
                            <TableCell style={{ padding: '0.3rem 0.5rem' }}>
                              <Typography style={{ fontSize: '0.88rem', fontWeight: '600', color: headingBlue }}>
                                {member.name}
                              </Typography>
                              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                                <Typography style={{ fontSize: '0.78rem', color: '#555' }}>
                                  +91 {member.contactNumber}
                                </Typography>
                                <Tooltip title="Copy Contact">
                                  <IconButton
                                    size="small"
                                    onClick={() => navigator.clipboard.writeText(member.contactNumber)}
                                    style={{ padding: 0, color: headingBlue }}
                                  >
                                    <ContentCopy style={{ fontSize: 13 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>

                            {/* 2. District & State */}
                            <TableCell style={{ padding: '0.3rem 0.5rem', textAlign: 'center' }}>
                              <Typography style={{ fontSize: '0.85rem', fontWeight: '600', color: headingBlue }}>
                                {member.district}
                              </Typography>
                              <Typography style={{ fontSize: '0.75rem', color: '#666' }}>
                                {member.state}
                              </Typography>
                            </TableCell>

                            {/* 3. District Head */}
                            <TableCell style={{ padding: '0.3rem 0.5rem' }}>
                              <Typography style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                                {member.districtHeadName || '-'}
                              </Typography>
                              {distContact ? (
                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.2rem' }}>
                                  <Typography style={{ fontSize: '0.75rem', color: '#555' }}>
                                    +91 {distContact}
                                  </Typography>
                                  <Tooltip title="Copy Contact">
                                    <IconButton
                                      size="small"
                                      onClick={() => navigator.clipboard.writeText(distContact)}
                                      style={{ padding: 0, color: headingBlue }}
                                    >
                                      <ContentCopy style={{ fontSize: 11 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Typography style={{ fontSize: '0.75rem', color: '#888' }}>-</Typography>
                              )}
                            </TableCell>

                            {/* 4. State Head */}
                            <TableCell style={{ padding: '0.3rem 0.5rem' }}>
                              <Typography style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                                {member.stateHeadName || '-'}
                              </Typography>
                              {stateContact ? (
                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.2rem' }}>
                                  <Typography style={{ fontSize: '0.75rem', color: '#555' }}>
                                    +91 {stateContact}
                                  </Typography>
                                  <Tooltip title="Copy Contact">
                                    <IconButton
                                      size="small"
                                      onClick={() => navigator.clipboard.writeText(stateContact)}
                                      style={{ padding: 0, color: headingBlue }}
                                    >
                                      <ContentCopy style={{ fontSize: 11 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Typography style={{ fontSize: '0.75rem', color: '#888' }}>-</Typography>
                              )}
                            </TableCell>

                            {/* 5. Action */}
                            <TableCell style={{ padding: '0.3rem 0.5rem', textAlign: 'center' }}>
                              {!hasVoted ? (
                                <Tooltip title="Send WhatsApp Vote Reminder">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const progName = activePollDetails?.programName || 'NDM Poll';
                                      const msg = `Assalamalikum, ${member.name},\n\nYou have not yet cast your vote for the "${progName}". Please cast your vote here:\n${votingUrl}\n\nThank you!\nWORK Team`;
                                      window.open(
                                        `https://wa.me/+91${member.contactNumber}?text=${encodeURIComponent(msg)}`,
                                        '_blank'
                                      );
                                    }}
                                    style={{ color: green, padding: '0.2rem' }}
                                  >
                                    <WhatsApp style={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Chip
                                  label="Voted"
                                  size="small"
                                  style={{
                                    backgroundColor: Donegreen,
                                    color: green,
                                    fontSize: '0.7rem',
                                    fontWeight: '700',
                                    height: '1.2rem',
                                    borderRadius: '3px',
                                  }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: maroon }}>
                          {isMembersLoading ? (
                            <CircularProgress size={24} style={{ color: headingBlue }} />
                          ) : (
                            'No Records Found'
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Consistent 3-Section Pagination Bar */}
              <Box style={{ display: 'flex', justifyContent: 'center', padding: '0.2rem 0rem 0.05rem 0rem', flexShrink: 0, marginTop: 'auto', borderTop: '1px solid #D9D9D9' }}>
                {/* 1. Record Count (Left) */}
                <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '0rem 0.5rem 0rem 0.9rem' }}>
                  <Typography style={{ color: maroon, fontSize: '0.9rem' }}>
                    {totalRecords > 0 ? (page - 1) * offset + 1 : 0} -{' '}
                    {Math.min(page * offset, totalRecords)} of {totalRecords}
                  </Typography>
                </Box>

                {/* 2. Page Controls (Center) */}
                <Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <KeyboardArrowLeft style={{ color: page === 1 ? mediumGrey : headingBlue }} />
                  </IconButton>

                  <Button
                    variant="contained"
                    style={{
                      minWidth: '0rem',
                      width: '1.5rem',
                      height: '1.5rem',
                      borderRadius: '50%',
                      backgroundColor: headingBlue,
                      color: 'white',
                      padding: '0rem',
                      fontSize: '0.8rem',
                    }}
                  >
                    {page}
                  </Button>

                  <IconButton
                    size="small"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <KeyboardArrowRight style={{ color: page >= totalPages ? mediumGrey : headingBlue }} />
                  </IconButton>
                </Box>

                {/* 3. Offset Dropdown (Right) */}
                <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', padding: '0rem 0.6rem 0rem 0rem' }}>
                  <CustomDropDown
                    list={tableOffsets}
                    value={offset}
                    setFunc={(val) => {
                      setOffset(val);
                      setPage(1);
                    }}
                    regular={true}
                    width="5rem"
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PollStats;
